/**
 * Users page — office user directory + invite flow.
 *
 * Mock-aware: reads `mockOfficeUsers` and supports local-state invite +
 * activate/deactivate + role change. Wired to `useMockData()` so the same
 * shell can flip to InstantDB once the live pipeline lands.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, UserCog, UserPlus } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import {
  Badge,
  Button,
  DataTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Switch,
  cn,
  toast,
} from "@bizop/ui-kit";
import type { RoleKey } from "@bizop/shared/rbac";

import { PageHeader } from "../../../components/PageHeader";
import {
  UserInviteForm,
  type UserInviteValues,
} from "../../../components/forms/UserInviteForm";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockOfficeBranches,
  mockOfficeUsers,
  type MockOfficeUser,
} from "../../../lib/rolesMock";
import { ROLE_LABELS, SELECTABLE_DEMO_ROLES } from "../../../state/roleStore";

export const Route = createFileRoute("/_app/people/users")({
  component: UsersPage,
});

function relativeTime(ms: number | null): string {
  if (ms === null) return "Never";
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(ms).toLocaleDateString("en-BD");
}

function UsersPage() {
  const [officeUsers] = usePersistentMockState<MockOfficeUser[]>("officeUsers", mockOfficeUsers);
  const [users, setUsers] = useState<MockOfficeUser[]>(() => [
    ...officeUsers,
  ]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [detailUser, setDetailUser] = useState<MockOfficeUser | null>(null);

  function handleInvite(values: UserInviteValues) {
    const exists = users.some(
      (u) => u.email.toLowerCase() === values.email.toLowerCase(),
    );
    if (exists) {
      toast.error(`A user with ${values.email} already exists`);
      return;
    }
    const branch = mockOfficeBranches.find((b) => b.id === values.branchId);
    const newUser: MockOfficeUser = {
      _id: `user_local_${Date.now().toString(36)}`,
      fullName: values.fullName,
      email: values.email,
      userType: "office",
      roleKey: values.roleKey,
      branchId: branch?.id ?? null,
      branchLabel: branch?.displayName ?? null,
      lastLoginAt: null,
      isActive: true,
      createdAt: Date.now(),
    };
    setUsers((prev) => [newUser, ...prev]);
    setInviteOpen(false);
    toast.success(`Invited ${values.fullName}`, {
      description: `${ROLE_LABELS[values.roleKey]} · invite sent to ${values.email}`,
    });
  }

  function handleRoleChange(userId: string, role: RoleKey) {
    setUsers((prev) =>
      prev.map((u) => (u._id === userId ? { ...u, roleKey: role } : u)),
    );
    if (detailUser?._id === userId) {
      setDetailUser({ ...detailUser, roleKey: role });
    }
    toast.success(`Role updated to ${ROLE_LABELS[role]}`);
  }

  function handleActiveToggle(userId: string, next: boolean) {
    setUsers((prev) =>
      prev.map((u) => (u._id === userId ? { ...u, isActive: next } : u)),
    );
    if (detailUser?._id === userId) {
      setDetailUser({ ...detailUser, isActive: next });
    }
    toast.success(next ? "User activated" : "User deactivated");
  }

  const columns = useMemo<ColumnDef<MockOfficeUser>[]>(
    () => [
      {
        accessorKey: "fullName",
        header: "Name",
        cell: ({ row }) => (
          <span className="font-medium text-(--text-primary)">
            {row.original.fullName}
          </span>
        ),
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-(--text-secondary)">
            {row.original.email}
          </span>
        ),
        meta: { mono: true },
      },
      {
        accessorKey: "userType",
        header: "User type",
        cell: ({ row }) => (
          <Badge variant="neutral" className="capitalize">
            {row.original.userType}
          </Badge>
        ),
      },
      {
        accessorKey: "roleKey",
        header: "Role",
        cell: ({ row }) => (
          <RoleBadge roleKey={row.original.roleKey} />
        ),
      },
      {
        id: "branch",
        header: "Branch",
        cell: ({ row }) => (
          <span className="text-(--text-secondary)">
            {row.original.branchLabel ?? "—"}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        accessorKey: "lastLoginAt",
        header: "Last login",
        cell: ({ row }) => (
          <span className="text-[12px] text-(--text-tertiary) tabular-nums">
            {relativeTime(row.original.lastLoginAt)}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        accessorKey: "isActive",
        header: "Status",
        cell: ({ row }) => (
          <StatusPill variant={row.original.isActive ? "success" : "neutral"}>
            {row.original.isActive ? "Active" : "Disabled"}
          </StatusPill>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Users"
        description="Office staff with access to BizOP. Dealers self-onboard via the dealer portal."
        actions={
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4" /> Invite user
          </Button>
        }
      />

      <DataTable
        data={users}
        columns={columns}
        getRowId={(r) => r._id}
        onRowClick={(row) => setDetailUser(row.original)}
        emptyTitle="No users yet"
        emptyDescription="Invite the first office user to start delegating work."
        emptyIcon={<UserCog className="h-5 w-5" aria-hidden />}
        emptyAction={
          <Button onClick={() => setInviteOpen(true)}>
            <Plus className="h-4 w-4" /> Invite user
          </Button>
        }
      />

      {/* Invite */}
      <Sheet open={inviteOpen} onOpenChange={setInviteOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>Invite a user</SheetTitle>
            <SheetDescription>
              They'll receive an email with a magic link. Permissions follow
              from the role.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <UserInviteForm
              branches={mockOfficeBranches}
              onSubmit={handleInvite}
              onCancel={() => setInviteOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Detail */}
      <Sheet
        open={detailUser !== null}
        onOpenChange={(open) => {
          if (!open) setDetailUser(null);
        }}
      >
        <SheetContent side="right" size="md">
          {detailUser ? (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <span>{detailUser.fullName}</span>
                  <RoleBadge roleKey={detailUser.roleKey} />
                </SheetTitle>
                <SheetDescription>
                  <span className="font-mono">{detailUser.email}</span>
                  {detailUser.branchLabel
                    ? ` · ${detailUser.branchLabel}`
                    : ""}
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <div className="flex flex-col gap-5">
                  <DetailRow
                    label="Last login"
                    value={relativeTime(detailUser.lastLoginAt)}
                  />
                  <DetailRow
                    label="Created"
                    value={new Date(detailUser.createdAt).toLocaleDateString(
                      "en-BD",
                      { year: "numeric", month: "short", day: "2-digit" },
                    )}
                  />
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                      Role
                    </span>
                    <Select
                      value={detailUser.roleKey}
                      onValueChange={(v) =>
                        handleRoleChange(detailUser._id, v as RoleKey)
                      }
                    >
                      <SelectTrigger aria-label="Role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SELECTABLE_DEMO_ROLES.map((r) => (
                          <SelectItem key={r} value={r}>
                            {ROLE_LABELS[r]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-(--text-tertiary)">
                      Changing role updates the user's permissions immediately.
                    </p>
                  </div>
                  <div className="flex items-center justify-between rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-3">
                    <div className="min-w-0">
                      <div className="text-[13px] font-medium text-(--text-primary)">
                        Account active
                      </div>
                      <div className="text-[11px] text-(--text-tertiary)">
                        Disabling locks the user out without removing history.
                      </div>
                    </div>
                    <Switch
                      checked={detailUser.isActive}
                      onCheckedChange={(v) =>
                        handleActiveToggle(detailUser._id, v)
                      }
                      aria-label="Toggle account active"
                    />
                  </div>
                </div>
              </SheetBody>
              <SheetFooter>
                <Button
                  variant="outline"
                  onClick={() => setDetailUser(null)}
                >
                  Close
                </Button>
              </SheetFooter>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function RoleBadge({ roleKey }: { roleKey: RoleKey }) {
  const tone = ROLE_TONES[roleKey] ?? "neutral";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium",
        TONE_CLASSES[tone],
      )}
    >
      {ROLE_LABELS[roleKey]}
    </span>
  );
}

const ROLE_TONES: Record<RoleKey, "accent" | "info" | "warning" | "success" | "neutral"> = {
  owner: "accent",
  admin: "info",
  finance: "warning",
  factory_manager: "info",
  office_manager: "info",
  sales: "success",
  warehouse: "neutral",
  dealer: "neutral",
};

const TONE_CLASSES: Record<string, string> = {
  accent:
    "bg-(--accent-soft) text-(--accent) border-(--border-subtle)",
  info: "bg-(--info-soft) text-(--info) border-(--border-subtle)",
  warning: "bg-(--warning-soft) text-(--warning) border-(--border-subtle)",
  success: "bg-(--success-soft) text-(--success) border-(--border-subtle)",
  neutral:
    "bg-(--bg-canvas) text-(--text-secondary) border-(--border-subtle)",
};

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </span>
      <span className="text-[13px] text-(--text-primary)">{value}</span>
    </div>
  );
}
